import { FilterCapabilityConfig } from '../types/baseFilterTypes'

/**
 * Generic column capabilities interface
 */
export interface ColumnCapabilities {
  /** Column supports sorting */
  sortable: boolean
  /** Column supports filtering */
  filterable: boolean
  /** Column supports grouping */
  groupable: boolean
  /** Column supports aggregation */
  aggregable?: boolean
  /** Column supports aggregation functions */
  availableAggregationFunctions?: string[]
}

/**
 * Configuration for creating column capabilities - frontend only
 */
export type CreateColumnCapabilitiesConfig = {
  /** The filter configuration containing field and sortable field definitions */
  filterConfig: FilterCapabilityConfig
  /** Column-specific capability overrides */
  columnOverrides?: Partial<Record<string, ColumnCapabilities>>
}

/**
 * Creates column capabilities configuration from filter config - frontend only utility
 */
export function createColumnCapabilities(config: CreateColumnCapabilitiesConfig): Record<string, ColumnCapabilities> {
  const sortableFields = config.filterConfig.sortableFields ?? {}
  const groupableFields = config.filterConfig.groupableFields ?? {}

  // Derive all column names from filter config
  const allColumns = [...Object.keys(config.filterConfig.filterableFields), ...Object.keys(sortableFields)]

  // Generate base column capabilities directly
  const baseCapabilities: Record<string, ColumnCapabilities> = {}
  allColumns.forEach((column) => {
    const fieldConfig = config.filterConfig.filterableFields[column]
    baseCapabilities[column] = {
      sortable: column in sortableFields,
      filterable: !!fieldConfig,
      groupable: column in groupableFields,
    }
  })

  // Apply column-specific overrides
  return {
    ...baseCapabilities,
    ...config.columnOverrides,
  } as Record<string, ColumnCapabilities>
}
